import { DispositionLeaf, DispositionTree } from '../@types/connect.type';

// Walks the nested disposition-tree shape returned by the support lambda
// and produces a flat list of leaves. Each leaf carries its breadcrumb
// trail (used to render group headers + the dotted path posted back as
// `n-disposition`).
//
// Tree shape per the legacy bundle: keys are categories; string / null
// values are leaves; nested objects are sub-categories. Recursion is
// unbounded but in practice categories are 2–4 levels deep.

export const flattenDispositionTree = (
  tree: DispositionTree | null | undefined,
  trail: string[] = []
): DispositionLeaf[] => {
  if (!tree) return [];
  const leaves: DispositionLeaf[] = [];
  for (const key of Object.keys(tree).sort()) {
    const value = tree[key];
    const nextTrail = [...trail, key];
    if (typeof value === 'string' || value === null) {
      leaves.push({
        path: nextTrail.join('.'),
        label: typeof value === 'string' && value.length > 0 ? value : key,
        trail: nextTrail,
      });
    } else {
      leaves.push(...flattenDispositionTree(value, nextTrail));
    }
  }
  return leaves;
};

// Group leaves by their immediate parent category for compact rendering
// (e.g. all "Escalations" leaves under one heading regardless of how deep
// they are within that branch).
export const groupLeavesByRoot = (
  leaves: DispositionLeaf[]
): Array<{ root: string; leaves: DispositionLeaf[] }> => {
  const map = new Map<string, DispositionLeaf[]>();
  for (const leaf of leaves) {
    const root = leaf.trail[0] ?? 'Other';
    const bucket = map.get(root) ?? [];
    bucket.push(leaf);
    map.set(root, bucket);
  }
  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([root, list]) => ({ root, leaves: list }));
};
